import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule, FormControlName } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { AuthService } from '../../@services/auth.service';
import { BillingService } from '../../@services/billing.service';
import { ToastService } from '../../@services/toast.service';
import { environment as env } from '../../../environments/environment';

@Component({
  selector: 'app-billing-reports',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './billing-reports.component.html',
  styleUrl: './billing-reports.component.scss'
})
export class BillingReportsComponent {
  month_keys: string[] = [];
  grouped_scanners: Record<string, any[]> = {};
  full_report: boolean = false;
  mta_live_data: boolean = false;

  // IT-F2-157 2026-10-30 — Billable vs Entitlement toggle. Default: true
  // (Billable). Mike c/37bcd0e4 on IT-F2-432: can't bill customers for
  // op-mti grants that were "systems mistake" (SamCart default_scanner
  // fallback). Billable=true narrows T/C-source counts to users whose
  // UserTracking grant has a non-fallback CommerceEvents row backing it.
  // Entitlement=false returns the legacy T+C derivation (who HAS access
  // right now regardless of commerce attribution). Cells for pre-TTL
  // months (CommerceEvents TTL = 365d) come back as null and render as
  // "--" in the grid.
  billable_only: boolean = true;

  // IT-F2-157 2026-10-30 — Mike c/93e9c527: customer dropdown filter to
  // focus the grid on a single customer. Empty string = show all. Backend
  // now narrows per customer (c/99935e0b) so changing the dropdown
  // re-fetches; the full customer slug list for the dropdown comes from
  // all_customer_slugs (loaded once in ngOnInit) so narrow mode doesn't
  // strand the dropdown with only the currently-loaded customer.
  private _customer_filter: string = '';
  get customer_filter(): string { return this._customer_filter; }
  set customer_filter(v: string) {
    const prev = this._customer_filter;
    this._customer_filter = v || '';
    if (prev === this._customer_filter) return;
    // Keep ?customer=<slug> in sync (removed when back to All). The
    // other toggles (full_report / billable_only) are URL-read-only
    // on first paint — if admins want to share a Billable-off view
    // they can edit the URL. replaceUrl so dropdown fiddling doesn't
    // spam browser history.
    try {
      this.router?.navigate([], {
        relativeTo: this.route,
        queryParams: { customer: this._customer_filter || null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    } catch { /* router may be undefined pre-ngOnInit; no-op */ }
    // IT-F2-157 Mike c/99935e0b (2026-10-30) — backend now narrows per
    // customer, so changing the dropdown re-fetches a narrower (or
    // wider when going back to All) payload instead of just hiding
    // sections. Fire-and-forget; the loadingService toast surfaces
    // in-flight state.
    this.get_billing_report();
  }
  // IT-F2-157 Mike c/99935e0b (2026-10-30) — load the full customer slug
  // list once at init so the dropdown stays complete even when backend
  // narrows the current payload. Falls back to grouped_scanners keys if
  // the one-shot load fails.
  all_customer_slugs: string[] = [];
  get customer_options(): string[] {
    if (this.all_customer_slugs.length) return this.all_customer_slugs;
    return Object.keys(this.grouped_scanners).sort();
  }
  get visible_grouped_scanners(): Record<string, any[]> {
    if (!this._customer_filter) return this.grouped_scanners;
    const picked = this.grouped_scanners[this._customer_filter];
    return picked ? { [this._customer_filter]: picked } : {};
  }

  // Data-source citation table. Every row + column in the grid below is
  // populated from ONE of these six sources. The legend below the
  // toolbar renders each as a hover-annotated pill so admins can see
  // where a number came from without spelunking the backend code. Each
  // row's source code(s) come from `source_codes_for(customer, scanner)`
  // and render as small badges next to the row label with the same
  // hover copy. Mike 2026-07-06.
  sources = [
    {
      code: 'T',
      label: 'UserTracking',
      location:
        'MongoDB f2tech · F2-ADMIN.UserTracking (activation + deactivation events).\n' +
        'Written by record_client_activation on SSO validation + Kartra/SamCart webhooks.\n' +
        'Drives: Delphi Signal, MTA, QPP, Simpler Trading, TheoTrade, Trend Labs rows.',
    },
    {
      code: 'C',
      label: 'Cognito Cache',
      location:
        'Redis USER_CACHE:<pool_id> hash — walked via SMEMBERS F2:USER_INDEX_POOLS.\n' +
        'Per-(email, customer) gap-fill: for each user in Cognito whose (email, customer)\n' +
        'pair isn\'t already in UserTracking, emit a synthetic tracking log with\n' +
        'start_date = Cognito UserCreateDate. Covers direct-signup users on per-customer\n' +
        'pools that never route through record_client_activation.',
    },
    {
      code: 'P',
      label: 'Push Subs',
      location:
        'MongoDB f2tech · F2-ADMIN.AlphaShark_Push_Subscriptions (activation + deactivation events).\n' +
        'Query: { role: { $nin: ["admin", "client"] } }.\n' +
        'Drives: AlphaShark → Push Notifications row (and All Users / Unique Users under AlphaShark).',
    },
    {
      code: 'B',
      label: 'Beta List',
      location:
        'MongoDB LEGACY · F2-ADMIN.ClientInfo { client: "simpler-trading" }.\n' +
        'Reads primary_beta[] + secondary_beta[] arrays (secondary applies to 2025-09..2025-12 months).\n' +
        'Splits Simpler Trading Big Three into Standard Users vs Legacy Users (spt-b3 → spt-b3-beta).',
    },
    {
      code: 'L',
      label: 'Aftershock Logins',
      location:
        'MongoDB f2tech · F2-ADMIN.AuthLogins (get_users_by_login_month).\n' +
        'Query narrowed to scanner="mta-aftershock" per requested login_month range.\n' +
        'Drives: MTA → Aftershock Logins row (unique logins per month).',
    },
    {
      code: 'D',
      label: 'MTA Live Data',
      location:
        'Redis USER_CACHE:<pool_id> — read only when Include MTA Live Data Count is checked.\n' +
        'Equities: enabled MTA user with custom:live_data_access="true".\n' +
        'Options: same user set narrowed via mta_options_data_check(custom:scanners, custom:claims).\n' +
        'Drives: MTA → Live Data - Equities and Live Data - Options rows (last-month column only).',
    },
    {
      code: 'A',
      label: 'AuthLogins (all)',
      location:
        'MongoDB f2tech + legacy · F2-ADMIN.AuthLogins (every login event).\n' +
        'Aggregated per (customer, YYYY-MM) with America/New_York bucketing in\n' +
        '_get_logins_by_customer_month. NOT deduped per user — this is engagement\n' +
        'volume (total login events). For distinct-user count see Unique Users.\n' +
        'Drives: per-customer "Logins" row (IT-F2-157 c/d91a7aa3).',
    },
    {
      code: '$',
      label: 'Billable match',
      location:
        'MongoDB f2tech · F2-ADMIN.CommerceEvents (365-day TTL).\n' +
        'Active when the Billable toggle is on. For every T/C-source row we\n' +
        'require a non-fallback CommerceEvents activation row (action=activate,\n' +
        'result ∈ {success, partial, already_activated}, !details.\n' +
        'default_scanner_applied) at or before end-of-month. Rows without a\n' +
        'match are dropped from the Billable count. Months whose END falls\n' +
        'before the TTL cutoff show "--" instead of 0 so pre-TTL data isn\'t\n' +
        'misread as "no users". Mike c/37bcd0e4 on IT-F2-432: billing can\'t\n' +
        'count system-mistake grants (SamCart default_scanner fallbacks).',
    },
    {
      code: 'R',
      label: 'Redis last-login',
      location:
        'Redis F2:USER_CACHE:<pool_id> hash — Cognito custom:last_login\n' +
        'stamped on each login by update_user_last_login (f2-admin-service\n' +
        'user-management.service.ts:6228). Single ISO timestamp per user,\n' +
        'overwritten on each login — NOT a per-event stream.\n' +
        'Drives: ${customer} - Last Logins row for direct-pool customers\n' +
        '(option-pit / alpha-shark / strat-alerts / hima) whose AuthLogins\n' +
        'writer stopped at the v1 → v2 stack cutover 2026-06-29.\n' +
        'SEMANTICS CAVEAT: each user contributes AT MOST ONE data point to\n' +
        'their most-recent-month cell. Daily users do NOT re-count. Cells\n' +
        'are biased toward the current month and systematically undercount\n' +
        'historical engagement volume vs the mongo-sourced Logins row.\n' +
        'Mike c/04346d6d on IT-F2-157: Redis-sourced fallback where AuthLogins is empty.',
    },
  ];

  // Customer group keys that MAY receive Cognito-cache gap-fill in
  // addition to their UserTracking base. Post-2026-07-07 backfill of
  // .90 → .133 UserTracking, every backfilled customer has real
  // tracking data (T source) AND still gets any missing per-(email,
  // customer) users filled in from Cognito USER_CACHE (C source). Rows
  // for these customers are labeled T+C so the admin knows both feeds
  // contribute. Legacy-pool customers that never carried a per-customer
  // Cognito pool don't need C (their Cognito data lives in the legacy
  // pool and is either already reflected in UserTracking or not
  // reachable at all).
  private _tc_dual_source_customer_slugs = new Set<string>([
    'option-pit', 'alpha-shark', 'strat-alerts', 'hima-reddy', 'hima', 'qpp',
  ]);

  // Customer group keys that pick up P (push-subs) as a data source.
  // Backend now uses the canonical slug for both push and Cognito
  // paths (IT-F2-157) so this needs 'alpha-shark' — the SAME slug
  // downstream code uses everywhere. Keep 'alphashark' as a defensive
  // fallback in case a legacy record slipped through.
  private _push_subs_customers = new Set<string>(['alpha-shark', 'alphashark']);

  // IT-F2-157 c/f900fc7f (Mike 2026-10-01): keep the raw backend payload
  // so the Copy Debug button can dump exactly what the SPA received —
  // same diagnostic pattern as the compliance-report Copy Debug.
  raw_report: any = null;

  constructor(
    private billingService : BillingService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  async ngOnInit() {
    // IT-F2-157 Mike c/93e9c527 (2026-10-30) — pick up ?customer=<slug>
    // from the URL on first paint so a deep-linked billing-reports URL
    // boots focused on the right customer. Also mirror full_report +
    // billable_only so the shared URL is self-contained. Read all 3
    // params BEFORE assigning so the first customer_filter setter sees
    // the correct full_report + billable_only values when it rewrites
    // the URL (otherwise it would strip them).
    const q = this.route.snapshot.queryParamMap;
    const q_full = q.get('full_report');
    if (q_full === 'true' || q_full === '1') this.full_report = true;
    const q_billable = q.get('billable_only');
    if (q_billable === 'false' || q_billable === '0') this.billable_only = false;
    const q_customer = q.get('customer');
    // Direct backing-field write so we don't trigger the URL-rewrite
    // setter on first paint — ngOnInit is already seeded from the URL.
    if (q_customer !== null) this._customer_filter = q_customer;
    // IT-F2-157 Mike c/99935e0b (2026-10-30) — one-shot customer slug
    // load so the dropdown stays complete under backend narrowing.
    this._load_all_customer_slugs();
    await this.get_billing_report();
  }

  private async _load_all_customer_slugs() {
    try {
      const res = await fetch(`${env.apiUrl}/rest/admin/customers`, { credentials: 'include' });
      if (!res.ok) return;
      const customers: any = await res.json();
      if (!Array.isArray(customers)) return;
      this.all_customer_slugs = customers
        .map((c: any) => String(c?._id || '').trim())
        .filter(Boolean)
        .sort();
    } catch { /* silent — falls back to grouped_scanners keys */ }
  }

  ngOnDestroy(): void {}

  async refresh() {
    await this.get_billing_report();
  }

  async get_billing_report() {
    // IT-F2-157 Mike c/99935e0b (2026-10-30) — when ?customer=<slug>
    // is set, pass it so the backend skips the fleet-wide fan-out and
    // only computes rows for the picked customer.
    let res: any = await this.billingService.getBillingReport(
      this.full_report,
      this.mta_live_data,
      this.billable_only,
      this.customer_filter || undefined,
    );
    this.raw_report = res;
    const grouped: any = {};

    Object.keys(res).forEach(scanner => {
      const customer = res[scanner].customer || "N/A";

      if (!grouped[customer]) {
        grouped[customer] = [];
      }

      grouped[customer].push({
        scanner,
        months: res[scanner].months
      });
    });

    this.grouped_scanners = grouped;

    // Generate months keys
    const all_months = new Set<string>();
    Object.values(res).forEach((scanner: any) => {
      Object.keys(scanner.months).forEach(month => all_months.add(month));
    });
    this.month_keys = Array.from(all_months).sort();
  }

  format_month(input: string) {
    const [year_string, month_string] = input.split('-');
    
    const year = parseInt(year_string, 10);
    const month = parseInt(month_string, 10) - 1;

    const date = new Date(year, month, 1);

    return date.toLocaleString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  }

  // IT-F2-157 Mike c/6671c48a (2026-10-30) — scanner-row tooltip. Explains
  // sentinel keys (<customer>-member) + source-code semantics for every row.
  scanner_tooltip(name: string): string {
    if (/^.+-member$/.test(name)) {
      return 'Customer-level members with NO scanner access from the catalog. ' +
        'Users enabled in this customer\'s Cognito pool whose custom:scanners CSV ' +
        'doesn\'t intersect with any scanner this customer owns in F2-ADMIN.Scanners. ' +
        'Still bills (per Mike c/d2948f37: Cognito entitlement = authority) ' +
        'but surfaces here so the operator can see users whose scanner access ' +
        'is implicit via custom:customers only.';
    }
    return '';
  }

  format_scanner_name(name: string) {
    // IT-F2-157 Mike c/6671c48a (2026-10-30) — surface <customer>-member
    // sentinel scanner keys as a human-readable label.
    if (/^.+-member$/.test(name)) {
      return 'Customer members (no scanner access)';
    }
    let format_name = name;
    switch (name) {
      case 'as-os':
        format_name = 'Option Sniper';
        break;
      case 'delphi-sig':
        format_name = 'Delphi Signal';
        break;
      case 'mta-aftershock':
        format_name = 'Aftershock';
        break;
      case 'mta-war_room':
        format_name = 'War Room';
        break;
      case 'dps':
        format_name = 'Daily Profits';
        break;
      case 'mta-mw':
        format_name = 'Profit Sight';
        break;
      // IT-F2-157 Mike c/205a4693 (2026-10-30): the 'option-pit' →
      // 'Alpha AI' legacy mapping made the customer-level roll-up row
      // look like a duplicate of the actual 'op-alpha-ai' scanner row
      // (both rendered as "Alpha AI" with the same badge set). The
      // 'option-pit' key here is the customer-level row — render it as
      // the customer section header below handles the display, and the
      // scanner row proper (op-alpha-ai) now carries the "Alpha AI"
      // label.
      case 'op-alpha-ai':
        format_name = 'Alpha AI';
        break;
      case 'op-fingerprints':
        format_name = 'Fingerprints';
        break;
      case 'spt-b3':
        format_name = 'Big Three - Standard Users';
        break;
      case 'spt-b3-beta':
        format_name = 'Big Three - Legacy Users';
        break;
      case 'strat-alerts':
        format_name = 'Strat Scanner';
        break;
      case 'strat':
        format_name = 'Strat Watchlists';
        break;
      case 'gappers':
        format_name = 'Gappers Watchlists';
        break;
      case 'tt-gc':
        format_name = 'Genesis Cog';
        break;
      case 'tt-uoa':
        format_name = 'Ghost Prints';
        break;
      case 'tt-trinity':
        format_name = 'Trinity';
        break;
      // IT-F2-157: backend now emits canonical slugs (matching
      // F2-ADMIN.Customers._id) instead of display-name variants so the
      // grouped_scanners map has one bucket per customer. Sync the
      // scanner-key match list to those slugs; the display-name cases
      // stay for backward compat during the deploy window.
      case 'alpha-shark':
      case 'delphi-sig':
      case 'mta':
      case 'option-pit':
      case 'simpler-trading':
      case 'strat-alerts':
      case 'theo-trade':
      case 'trend-labs':
      case 'hima-reddy':
      case 'AlphaShark':
      case 'Delphi Signal':
      case 'MTA':
      case 'Option Pit':
      case 'Simpler Trading':
      case 'Strat Alerts':
      case 'TheoTrade':
        format_name = 'All Users';
        break;
      case 'alpha-shark - Unique':
      case 'delphi-sig - Unique':
      case 'mta - Unique':
      case 'option-pit - Unique':
      case 'simpler-trading - Unique':
      case 'strat-alerts - Unique':
      case 'theo-trade - Unique':
      case 'trend-labs - Unique':
      case 'hima-reddy - Unique':
      case 'AlphaShark - Unique':
      case 'Delphi Signal - Unique':
      case 'MTA - Unique':
      case 'Option Pit - Unique':
      case 'Simpler Trading - Unique':
      case 'Strat Alerts - Unique':
      case 'TheoTrade - Unique':
        format_name = 'Unique Users';
        break;
      default:
        // IT-F2-157 c/d91a7aa3 (Mike 2026-10-01): per-customer Logins
        // row. Backend emits `<customer> - Logins` for every customer
        // with AuthLogins activity; relabel to the compact "Logins"
        // for the per-customer section.
        if (/ - Logins$/.test(name)) format_name = 'Logins';
        break;
    }

    return format_name;
  }

  // Return the source code(s) that fed a row. Rendered as small badges
  // next to the row's label; each badge is title-tipped with the exact
  // backing location from `sources`. Some rows have multiple sources —
  // spt-b3 (T + B for beta split), 'All Customers' (aggregate of all
  // producers). Order = display priority (primary first).
  source_codes_for(customer_key: string, scanner_key: string): string[] {
    const k = String(scanner_key || '');
    // Scanner-key overrides — these rows have a dedicated producer
    // regardless of customer group.
    if (k === 'Push Notifications') return ['P'];
    if (k === 'Aftershock Logins') return ['L'];
    if (k === 'Live Data - Equities' || k === 'Live Data - Options') return ['D'];
    if (k === 'spt-b3' || k === 'spt-b3-beta') return ['T', 'B'];
    // IT-F2-157 c/d91a7aa3 (Mike 2026-10-01): per-customer Logins row
    // is populated from AuthLogins (code A) across both main + legacy.
    if (/ - Logins$/.test(k)) return ['A'];
    // IT-F2-157 Mike c/04346d6d (2026-10-30): per-customer Last Logins
    // row is Redis-sourced (custom:last_login on F2:USER_CACHE:<pool_id>)
    // for direct-pool customers.
    if (/ - Last Logins$/.test(k)) return ['R'];

    const cust_lc = String(customer_key || '').toLowerCase();
    // IT-F2-157 2026-10-30 — '$' Billable match badge prepends to any
    // row whose T/C source is being filtered through CommerceEvents
    // when the Billable toggle is on. Push subs / logins / live-data
    // rows don't take the badge (not commerce-attribution-filtered).
    const bill_prefix = this.billable_only ? ['$'] : [];
    // 'All Customers' is the roll-up row — aggregates over every
    // producer that feeds a customer. List every code so hover reveals
    // the full stack.
    if (cust_lc === 'all customers') return [...bill_prefix, 'T', 'C', 'P', 'L'];
    // AlphaShark display-name group is push-subs sourced.
    if (this._push_subs_customers.has(cust_lc)) return ['P'];
    // Customers with a per-customer Cognito pool: base is UserTracking
    // (T) — real activation/deactivation events; plus C gap-fill from
    // Cognito USER_CACHE for any per-(email, customer) users not yet
    // in UserTracking (direct-signup users on their per-customer pool).
    if (this._tc_dual_source_customer_slugs.has(cust_lc)) return [...bill_prefix, 'T', 'C'];
    // Everything else = UserTracking only (Delphi Signal, MTA, Simpler
    // Trading, TheoTrade, Trend Labs — still on legacy pool).
    return [...bill_prefix, 'T'];
  }

  // Compact tooltip for a source code. Same location string as the
  // legend pill so hovering either surfaces the same detail.
  source_tooltip(code: string): string {
    const s = this.sources.find(x => x.code === code);
    if (!s) return code;
    return `${s.label}\n\n${s.location}`;
  }

  // IT-F2-157 item 10e3cea4 — shareable admin URL for a single cell's
  // breakout detail. Backend endpoint is admin-gated; this URL is
  // meaningful only inside an authed admin session.
  breakout_url(customer: string, scanner: string, month: string): string {
    const q = new URLSearchParams({ customer, scanner, month });
    // IT-F2-157 2026-10-30 — carry the Billable toggle through to the
    // breakout page so a drill-down from a Billable cell shows the
    // filtered row set + non-billable tray matching that count.
    if (this.billable_only) q.set('billable_only', 'true');
    return `/admin/billing-reports/breakout?${q.toString()}`;
  }

  // IT-F2-157 Mike c/97f291da (2026-10-30) — row-title click opens a
  // 6-month aggregate breakout spanning the latest 6 months_keys the
  // grid currently has loaded. Backend accepts ?months=M1,M2,...
  // comma-separated and widens the window.
  breakout_url_range(customer: string, scanner: string): string {
    const q = new URLSearchParams({ customer, scanner });
    const tail = (this.month_keys || []).slice(-6);
    if (tail.length) q.set('months', tail.join(','));
    if (this.billable_only) q.set('billable_only', 'true');
    return `/admin/billing-reports/breakout?${q.toString()}`;
  }

  // IT-F2-157 c/f900fc7f (Mike 2026-10-01): Copy Debug button. Dumps
  // the raw backend payload + current filters + a per-customer summary
  // of which row keys came back, so missing rows (like the Logins row
  // on an unexpected customer slug) are visible without needing a
  // prod Mongo query.
  async copy_debug() {
    const now_iso = new Date().toISOString();
    const raw = this.raw_report || {};
    const customer_section_summary: any = {};
    for (const [key, cust_rows] of Object.entries(this.grouped_scanners || {})) {
      const rows = (cust_rows as any[]) || [];
      customer_section_summary[key] = {
        row_count: rows.length,
        row_keys: rows.map(r => r?.scanner),
        has_logins_row: rows.some(r => / - Logins$/.test(String(r?.scanner || ''))),
      };
    }

    // IT-F2-157 Mike c/cc5de0dc (2026-10-30): when the operator has
    // picked specific rows via the row checkboxes, scope the raw
    // payload to just those (keyed by backend row key = scanner value).
    // The summary + customer_sections still cover the full view so the
    // operator can see what WAS available vs what they picked.
    let payload_raw: any = raw;
    const picked_rows: string[] = [];
    if (this.selected_report_rows.size > 0) {
      payload_raw = {};
      for (const [cust_key, cust_rows] of Object.entries(this.grouped_scanners || {})) {
        for (const r of ((cust_rows as any[]) || [])) {
          const sel_key = `${cust_key}::${r?.scanner}`;
          if (!this.selected_report_rows.has(sel_key)) continue;
          picked_rows.push(sel_key);
          const backend_key = String(r?.scanner || '');
          if (backend_key in raw) payload_raw[backend_key] = raw[backend_key];
        }
      }
    }

    const snapshot = {
      timestamp: now_iso,
      label: 'Billing Reports — Copy Debug',
      url: window.location.href,
      filters: {
        full_report: this.full_report,
        mta_live_data: this.mta_live_data,
        billable_only: this.billable_only,
        customer_filter: this.customer_filter || null,
      },
      month_keys: this.month_keys,
      counts: {
        raw_row_keys: Object.keys(raw).length,
        customers_in_grid: Object.keys(this.grouped_scanners || {}).length,
        selected_rows: this.selected_report_rows.size,
        payload_row_keys: Object.keys(payload_raw).length,
      },
      selected: this.selected_report_rows.size > 0 ? picked_rows : null,
      customer_sections: customer_section_summary,
      // Full raw payload (or scoped to selected rows) last so the
      // summary is visible at the top without scrolling past 5000+ lines.
      raw_backend_payload: payload_raw,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      this.toast.success(
        'Debug snapshot copied',
        this.selected_report_rows.size > 0
          ? `${this.selected_report_rows.size} selected row(s) / ${snapshot.counts.customers_in_grid} customer sections`
          : `${snapshot.counts.raw_row_keys} backend row keys / ${snapshot.counts.customers_in_grid} customer sections`,
      );
    } catch (e: any) {
      this.toast.error('Clipboard blocked', e?.message || 'browser rejected the copy');
    }
  }

  // IT-F2-157 Mike c/cc5de0dc (2026-10-30) — per-row checkbox selection
  // on the billing-reports grid so Copy Debug can scope to specific
  // row(s) for feedback. Key = "<customer_key>::<scanner_key>" since
  // the same scanner key (e.g. "All Scanners" / "Unique Users") can
  // appear under multiple customer sections.
  selected_report_rows = new Set<string>();
  report_row_key(customer_key: string, scanner_key: string): string {
    return `${customer_key}::${scanner_key}`;
  }
  is_report_row_selected(customer_key: string, scanner_key: string): boolean {
    return this.selected_report_rows.has(this.report_row_key(customer_key, scanner_key));
  }
  toggle_report_row(customer_key: string, scanner_key: string, ev: Event) {
    const k = this.report_row_key(customer_key, scanner_key);
    const checked = (ev.target as HTMLInputElement).checked;
    if (checked) this.selected_report_rows.add(k);
    else this.selected_report_rows.delete(k);
  }
  clear_report_selection() {
    this.selected_report_rows.clear();
  }
}
